/* /s/:handle/catalog — public, shareable seller CATALOG webpage.
 *
 * A full store-style catalog: branded header, search, category + brand
 * filters, responsive product grid and a guest cart → checkout (reuses the
 * existing /storefront/:slug/checkout endpoint). No auth needed to browse.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShoppingCart, Plus, Minus, Trash2, Package, ArrowRight, Loader2,
  CheckCircle2, X, Lock, Store, ArrowLeft,
  MapPin, Clock, Truck, BadgeCheck, ShieldCheck,
} from "lucide-react";
import {
  getCart, addToCart, updateQty, removeFromCart, effectivePrice,
} from "../lib/cart";
import {
  CatalogLayoutShell, CatalogProductCard, CatalogTextTable, useAttrFacets,
} from "../components/catalog-shared";

const INTENT_KEY = "verqo_storefront_checkout_intent";

const SEARCH_FIELDS = [
  { value: "all", label: "All" },
  { value: "name", label: "Name" },
  { value: "sku", label: "SKU" },
  { value: "upc", label: "UPC" },
  { value: "product_type", label: "Type" },
  { value: "size", label: "Size" },
];
const SORT_OPTIONS = [
  { value: "relevance", label: "Sort: Featured" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "new", label: "New arrivals" },
  { value: "top", label: "Top sellers" },
];
const CATALOG_TESTIDS = {
  search: "storefront-catalog-search",
  quickPills: "storefront-catalog-quickpills",
  badge: (k) => `storefront-badge-${k}`,
  topCats: "storefront-catalog-topcats",
  topCat: (k) => `storefront-cat-${k}`,
  sidebar: "storefront-catalog-sidebar",
  avail: (k) => `storefront-avail-${k}`,
  fulfill: (k) => `storefront-fulfill-${k}`,
  priceSlider: "storefront-price-slider",
  moqSlider: "storefront-moq-slider",
  clearFilters: "storefront-clear-filters",
  viewToggle: "storefront-view-toggle",
  view: (k) => `storefront-view-${k}`,
};

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}

export default function StorefrontCatalog() {
  const { handle } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const [profile, setProfile] = useState(null);
  const [feed, setFeed] = useState(null); // { enabled, seller_id, products }
  const [err, setErr] = useState(null);

  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [badgeSet, setBadgeSet] = useState(new Set());
  const [cats, setCats] = useState(new Set());
  const [brandSet, setBrandSet] = useState(new Set());
  const [fulfillment, setFulfillment] = useState("all");
  const [maxPrice, setMaxPrice] = useState(500);
  const [minMoq, setMinMoq] = useState(1);
  const [sort, setSort] = useState("relevance");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;

  // buyer-card interaction state
  const [qtyMap, setQtyMap] = useState({});
  const [selected, setSelected] = useState({});
  const [detail, setDetail] = useState(null);

  const [cartItems, setCartItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const sellerId = feed?.seller_id || null;
  const sellerName = profile?.name || feed?.seller_name;
  const b = profile?.branding || {};
  const primary = b.primary_color || "#0F1419";
  const accent = b.accent_color || "#e8ff3a";

  const refreshCart = useCallback(() => {
    setCartItems(getCart().filter(x => sellerId && x.seller_id === sellerId));
  }, [sellerId]);

  useEffect(() => {
    setProfile(null); setFeed(null); setErr(null);
    api.get(`/public/seller/${handle}`)
       .then(r => setProfile(r.data))
       .catch(e => setErr(e.response?.data?.detail || "Storefront not found"));
    api.get(`/public/seller/${handle}/products`)
       .then(r => setFeed(r.data))
       .catch(() => setFeed({ enabled: false, products: [] }));
  }, [handle]);

  useEffect(() => { refreshCart(); }, [refreshCart, feed]);
  useEffect(() => {
    const fn = () => refreshCart();
    window.addEventListener("verqo-cart-change", fn);
    window.addEventListener("storage", fn);
    return () => {
      window.removeEventListener("verqo-cart-change", fn);
      window.removeEventListener("storage", fn);
    };
  }, [refreshCart]);

  const products = useMemo(() => feed?.products || [], [feed]);

  const categories = useMemo(() => {
    const m = new Map();
    products.forEach(p => { if (p.category) m.set(p.category, (m.get(p.category) || 0) + 1); });
    return [...m.entries()].sort((a, c) => c[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [products]);

  const productBrands = useMemo(() => {
    const m = new Map();
    products.forEach(p => { if (p.brand) m.set(p.brand, (m.get(p.brand) || 0) + 1); });
    return [...m.entries()].sort((a, c) => c[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [products]);

  const { attrFilters, applyAttrFacets, attrSelKey } = useAttrFacets(products);

  const filtered = useMemo(() => {
    let r = [...products];
    if (badgeSet.size > 0) r = r.filter(p => badgeSet.has(p.badge));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(p => {
        if (searchField === "name") return p.name?.toLowerCase().includes(q);
        if (searchField === "sku")  return p.sku?.toLowerCase().includes(q);
        if (searchField === "upc")  return p.upc?.toLowerCase().includes(q);
        if (searchField === "product_type") return p.product_type?.toLowerCase().includes(q);
        if (searchField === "size") return String(p.size || "").toLowerCase().includes(q);
        return [p.name, p.sku, p.upc, p.brand, p.category, p.sub_category, p.product_type, p.size, p.gender, p.condition, p.variant, p.ean, p.asin].some(v => v != null && String(v).toLowerCase().includes(q));
      });
    }
    if (cats.size > 0) r = r.filter(p => cats.has(p.category));
    if (brandSet.size > 0) r = r.filter(p => brandSet.has(p.brand));
    r = applyAttrFacets(r);
    if (fulfillment !== "all") r = r.filter(p => p.fulfillment === fulfillment || p.fulfillment === "both");
    r = r.filter(p => (p.price || 0) <= maxPrice);
    r = r.filter(p => (p.moq || 1) >= minMoq);
    if (sort === "price_asc") r.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") r.sort((a, b) => b.price - a.price);
    else if (sort === "name_asc") r.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sort === "name_desc") r.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    else if (sort === "new") r.sort((a, b) => (b.badge === "new_arrival" ? 1 : 0) - (a.badge === "new_arrival" ? 1 : 0));
    else if (sort === "top") r.sort((a, b) => (b.badge === "top_seller" ? 1 : 0) - (a.badge === "top_seller" ? 1 : 0));
    return r;
  }, [products, search, searchField, badgeSet, cats, brandSet, applyAttrFacets, attrSelKey, fulfillment, maxPrice, minMoq, sort]);

  // Reset to page 1 whenever the result set changes.
  useEffect(() => { setPage(1); }, [search, searchField, badgeSet, cats, brandSet, attrSelKey, fulfillment, maxPrice, minMoq, sort]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const subtotal = useMemo(() => cartItems.reduce((s, x) => s + x.qty * effectivePrice(x), 0), [cartItems]);
  const count = useMemo(() => cartItems.reduce((s, x) => s + x.qty, 0), [cartItems]);
  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  const getQty = (p) => qtyMap[p.id] ?? (p.moq || 1);
  const setQ = (pid, n) => setQtyMap(prev => ({ ...prev, [pid]: Math.max(1, n) }));

  const onAdd = (p, qty) => {
    const finalQty = qty || p.moq || 1;
    addToCart({
      product_id: p.id, name: p.name, qty: finalQty, price: p.price,
      seller_id: p.seller_id, seller_name: sellerName || p.seller_name,
      image: p.image, moq: p.moq || 1, sku: p.sku, tiers: p.tiers || [],
      map_price: p.map_price ?? null,
    });
    refreshCart();
    toast.success(`${finalQty}× ${p.name} added to cart`);
  };

  const addSelected = () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return;
    ids.forEach(id => { const p = products.find(x => x.id === id); if (p) onAddSilent(p, getQty(p)); });
    setSelected({});
    refreshCart();
    toast.success(`${ids.length} item${ids.length !== 1 ? "s" : ""} added to cart`);
  };
  const onAddSilent = (p, qty) => addToCart({
    product_id: p.id, name: p.name, qty: qty || p.moq || 1, price: p.price,
    seller_id: p.seller_id, seller_name: sellerName || p.seller_name,
    image: p.image, moq: p.moq || 1, sku: p.sku, tiers: p.tiers || [], map_price: p.map_price ?? null,
  });
  const inc = (pid) => { const it = cartItems.find(x => x.product_id === pid); updateQty(pid, (it?.qty || 0) + 1); refreshCart(); };
  const dec = (pid) => { const it = cartItems.find(x => x.product_id === pid); updateQty(pid, Math.max(it?.moq || 1, (it?.qty || 0) - 1)); refreshCart(); };
  const drop = (pid) => { removeFromCart(pid); refreshCart(); };

  const doCheckout = useCallback(async () => {
    const items = getCart().filter(x => x.seller_id === sellerId);
    if (items.length === 0) return;
    setPlacing(true);
    try {
      const { data } = await api.post(`/storefront/${handle}/checkout`, {
        items: items.map(i => ({ product_id: i.product_id, qty: i.qty })),
      });
      const remaining = getCart().filter(x => x.seller_id !== sellerId);
      localStorage.setItem("verqo_cart", JSON.stringify(remaining));
      window.dispatchEvent(new CustomEvent("verqo-cart-change"));
      refreshCart();
      setCartOpen(false);
      setConfirmation({ order: data.order, invoice: data.invoice });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place order");
    } finally {
      setPlacing(false);
    }
  }, [handle, sellerId, refreshCart]);

  const startCheckout = () => {
    if (user?.role === "buyer") { doCheckout(); return; }
    if (user && user.role !== "buyer") { toast.error("Switch to a buyer account to place an order"); return; }
    try { localStorage.setItem(INTENT_KEY, JSON.stringify({ handle, ts: Date.now() })); } catch { /* ignore */ }
    const next = encodeURIComponent(`/s/${handle}/catalog?checkout=1`);
    nav(`/register?as=buyer&next=${next}`);
  };

  useEffect(() => {
    if (!feed || !sellerId) return;
    if (params.get("checkout") !== "1") return;
    if (user?.role !== "buyer") return;
    const hasItems = getCart().some(x => x.seller_id === sellerId);
    params.delete("checkout");
    setParams(params, { replace: true });
    localStorage.removeItem(INTENT_KEY);
    if (hasItems) doCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, sellerId, user]);

  if (err) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="max-w-md text-center">
        <h1 className="display text-3xl">Catalog not available</h1>
        <p className="text-foreground/60 mt-3">{err}</p>
        <Link to="/" className="inline-block mt-6 text-foreground underline" data-testid="storefront-catalog-back">Back home</Link>
      </div>
    </div>
  );
  if (!feed || !profile) return (
    <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-foreground/40"/></div>
  );

  const catalogDisabled = !feed.enabled;

  return (
    <div className="min-h-screen bg-secondary/20" data-testid="storefront-catalog-page">
      {/* Branded header */}
      <header className="relative overflow-hidden" style={{ background: primary }} data-testid="storefront-catalog-header">
        <div className="max-w-[1240px] mx-auto px-6 sm:px-8 py-8">
          <Link to={`/s/${handle}`} className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[12.5px] mb-5" data-testid="storefront-catalog-to-profile">
            <ArrowLeft className="w-3.5 h-3.5"/> {sellerName} storefront
          </Link>
          <div className="flex items-end justify-between gap-5 flex-wrap">
            <div className="flex items-center gap-4">
              {b.logo_square_url ? (
                <div className="w-16 h-16 rounded-2xl bg-white/95 grid place-items-center overflow-hidden shrink-0">
                  <img src={b.logo_square_url} alt={sellerName} className="w-full h-full object-cover"/>
                </div>
              ) : b.logo_url ? (
                <div className="bg-white/95 rounded-2xl px-4 py-3 inline-flex items-center shrink-0">
                  <img src={b.logo_url} alt={sellerName} className="max-h-12 max-w-[14rem] object-contain"/>
                </div>
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-white/15 text-white grid place-items-center text-2xl display shrink-0">
                  {(sellerName || "?").slice(0, 1)}
                </div>
              )}
              <div>
                <div className="text-[10.5px] mono uppercase tracking-[0.22em]" style={{ color: accent }}>Catalog</div>
                <h1 className="display text-3xl text-white tracking-tight mt-1">{sellerName}</h1>
                <div className="text-white/70 text-[13px] mt-1.5">
                  {catalogDisabled ? "Catalog is currently unavailable" : `${products.length} products available to order`}
                </div>
                {!catalogDisabled && <SellerQuickInfo profile={profile} handle={handle}/>}
              </div>
            </div>
            <Button onClick={() => setCartOpen(true)} data-testid="storefront-catalog-cart-button"
              className="rounded-full h-11 px-5 relative" style={{ background: accent, color: primary }}>
              <ShoppingCart className="w-4 h-4 mr-2"/>Cart
              {count > 0 && (
                <span data-testid="storefront-catalog-cart-count" className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-white text-foreground text-[11px] font-semibold">{count}</span>
              )}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1240px] mx-auto px-6 sm:px-8 py-7">
        {catalogDisabled ? (
          <div className="rounded-2xl border border-dashed border-foreground/20 bg-white/60 p-12 text-center" data-testid="storefront-catalog-empty">
            <Store className="w-6 h-6 text-foreground/40 mx-auto"/>
            <div className="display text-lg mt-3">This catalog isn't published yet</div>
            <div className="text-[13px] text-foreground/55 mt-1.5">Check back soon, or contact the seller directly.</div>
          </div>
        ) : (
          <CatalogLayoutShell
            testids={CATALOG_TESTIDS}
            search={search} setSearch={setSearch}
            searchField={searchField} setSearchField={setSearchField}
            searchFields={SEARCH_FIELDS} searchPlaceholder="Search products, SKU, UPC…"
            badgeSet={badgeSet} setBadgeSet={setBadgeSet}
            sort={sort} setSort={setSort} sortOptions={SORT_OPTIONS}
            categories={categories} cats={cats} setCats={setCats} totalCount={products.length}
            fulfillment={fulfillment} setFulfillment={setFulfillment}
            maxPrice={maxPrice} setMaxPrice={setMaxPrice} minMoq={minMoq} setMinMoq={setMinMoq}
            productBrands={productBrands} brandSet={brandSet} setBrandSet={setBrandSet}
            attrFilters={attrFilters}
            viewMode={viewMode} setViewMode={setViewMode} filteredCount={filtered.length}>
            {filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-foreground/15 bg-white/60 p-12 text-center" data-testid="storefront-catalog-no-results">
                <Package className="w-6 h-6 text-foreground/30 mx-auto"/>
                <div className="text-sm text-foreground/55 mt-3">No products match your filters.</div>
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="storefront-catalog-products">
                {pageRows.map(p => (
                  <CatalogProductCard key={p.id} mode="buyer" view="grid" p={p}
                    selected={selected[p.id]} onSelect={(v) => setSelected(s => ({ ...s, [p.id]: v }))}
                    qty={getQty(p)} setQty={(n) => setQ(p.id, n)}
                    unitMode="unit" setMode={() => {}}
                    onAdd={() => onAdd(p, getQty(p))} onDetails={() => setDetail(p)}/>
                ))}
              </div>
            ) : viewMode === "list" ? (
              <div className="space-y-2" data-testid="storefront-catalog-list">
                {pageRows.map(p => (
                  <CatalogProductCard key={p.id} mode="buyer" view="list" p={p}
                    selected={selected[p.id]} onSelect={(v) => setSelected(s => ({ ...s, [p.id]: v }))}
                    qty={getQty(p)} setQty={(n) => setQ(p.id, n)}
                    onAdd={() => onAdd(p, getQty(p))} onDetails={() => setDetail(p)}/>
                ))}
              </div>
            ) : (
              <CatalogTextTable mode="buyer" rows={pageRows}
                selected={selected} onSelect={(id, v) => setSelected(s => ({ ...s, [id]: v }))}
                getQty={getQty} setQty={setQ} onAdd={(p) => onAdd(p, getQty(p))} onDetails={setDetail}/>
            )}

            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-1.5 mt-8" data-testid="storefront-catalog-pagination">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="h-9 px-3 rounded-full border border-border bg-white text-[12.5px] disabled:opacity-40 hover:border-foreground/40 transition-colors inline-flex items-center gap-1" data-testid="storefront-page-prev">
                  <ArrowLeft className="w-3.5 h-3.5"/>Prev
                </button>
                {pageNumbers(page, pageCount).map((n, i) => n === "…" ? (
                  <span key={`e${i}`} className="px-2 text-foreground/40 text-[12.5px]">…</span>
                ) : (
                  <button key={n} onClick={() => setPage(n)} data-testid={`storefront-page-${n}`}
                    className={`h-9 min-w-9 px-2 rounded-full text-[12.5px] transition-colors ${page === n ? "bg-foreground text-background" : "border border-border bg-white text-foreground/70 hover:border-foreground/40"}`}>{n}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(pageCount, p + 1))} disabled={page === pageCount}
                  className="h-9 px-3 rounded-full border border-border bg-white text-[12.5px] disabled:opacity-40 hover:border-foreground/40 transition-colors inline-flex items-center gap-1" data-testid="storefront-page-next">
                  Next<ArrowRight className="w-3.5 h-3.5"/>
                </button>
              </div>
            )}
          </CatalogLayoutShell>
        )}
      </div>

      {/* Add-selected floating bar */}
      {selectedCount > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-30" data-testid="storefront-add-selected-bar">
          <Button onClick={addSelected} className="rounded-full h-12 px-6 shadow-lg bg-foreground text-background hover:bg-foreground/90">
            <Plus className="w-4 h-4 mr-1.5"/>Add {selectedCount} selected to cart
          </Button>
        </div>
      )}

      {/* Sticky mobile cart bar */}
      {count > 0 && (
        <div className="fixed bottom-4 inset-x-4 z-30 sm:hidden">
          <Button onClick={() => setCartOpen(true)} data-testid="storefront-catalog-cart-bar"
            className="w-full h-12 rounded-full shadow-lg justify-between px-5" style={{ background: primary, color: "#fff" }}>
            <span className="inline-flex items-center gap-2"><ShoppingCart className="w-4 h-4"/>{count} item{count !== 1 ? "s" : ""}</span>
            <span className="display">{money(subtotal)}</span>
          </Button>
        </div>
      )}

      {/* Cart drawer */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-md flex flex-col" data-testid="storefront-catalog-cart-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2"><Store className="w-4 h-4"/> {sellerName || "Your cart"}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-4">
            {cartItems.length === 0 ? (
              <div className="text-center py-16" data-testid="storefront-catalog-cart-empty">
                <div className="w-12 h-12 rounded-2xl bg-secondary grid place-items-center mx-auto"><ShoppingCart className="w-5 h-5 text-foreground/40"/></div>
                <div className="text-sm text-foreground/55 mt-4">Your cart is empty</div>
              </div>
            ) : (
              <div className="space-y-3">
                {cartItems.map(it => {
                  const eff = effectivePrice(it);
                  return (
                    <div key={it.product_id} className="flex gap-3 items-center" data-testid={`storefront-catalog-cart-row-${it.product_id}`}>
                      <div className="w-12 h-12 rounded-lg bg-secondary grid place-items-center shrink-0 overflow-hidden">
                        {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover"/> : <Package className="w-4 h-4 text-foreground/40"/>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate">{it.name}</div>
                        <div className="text-[11px] text-foreground/55 mono">{money(eff)} ea</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => dec(it.product_id)} className="w-7 h-7 rounded-full border border-border grid place-items-center hover:bg-secondary" data-testid={`storefront-catalog-dec-${it.product_id}`}><Minus className="w-3 h-3"/></button>
                        <Input type="number" value={it.qty} onChange={e => { updateQty(it.product_id, Math.max(it.moq || 1, parseInt(e.target.value) || 1)); refreshCart(); }} className="w-12 h-7 text-center mono text-[12px] px-1" data-testid={`storefront-catalog-qty-${it.product_id}`}/>
                        <button onClick={() => inc(it.product_id)} className="w-7 h-7 rounded-full border border-border grid place-items-center hover:bg-secondary" data-testid={`storefront-catalog-inc-${it.product_id}`}><Plus className="w-3 h-3"/></button>
                      </div>
                      <button onClick={() => drop(it.product_id)} className="text-foreground/40 hover:text-rose-700 ml-1" data-testid={`storefront-catalog-remove-${it.product_id}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <SheetFooter className="border-t border-border pt-4 flex-col gap-3 sm:flex-col sm:space-x-0">
            <div className="w-full flex items-center justify-between">
              <span className="text-sm text-foreground/60">Subtotal</span>
              <span className="display text-2xl" data-testid="storefront-catalog-cart-subtotal">{money(subtotal)}</span>
            </div>
            <Button onClick={startCheckout} disabled={cartItems.length === 0 || placing} data-testid="storefront-catalog-checkout-button"
              className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90">
              {placing ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
              {user?.role === "buyer" ? "Place order" : "Sign up to order"}<ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
            {(!user || user.role !== "buyer") && (
              <div className="w-full text-center text-[11px] text-foreground/45 flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3"/> Secure checkout · payment due only after the seller confirms
              </div>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ProductDetailModal p={detail} onClose={() => setDetail(null)} onAdd={onAdd}/>

      <StorefrontSuccessModal data={confirmation} onClose={() => setConfirmation(null)}
        onTrack={() => { setConfirmation(null); nav("/buyer/orders#journey"); }}/>
    </div>
  );
}

function SellerQuickInfo({ profile, handle }) {
  const prof = profile?.profile || {};
  const pol = profile?.policies || {};
  const trust = profile?.trust || {};
  const shipsFrom = prof.location || prof.ships_from;
  const leadDays = pol.default_lead_days;
  const items = [];
  if (shipsFrom) items.push({ icon: MapPin, label: shipsFrom });
  if (leadDays != null) items.push({ icon: Clock, label: `Ships in ${leadDays} day${leadDays === 1 ? "" : "s"}` });
  if (prof.ships_to) items.push({ icon: Truck, label: `Ships to ${prof.ships_to}` });
  if (trust.orders_fulfilled > 0) items.push({ icon: CheckCircle2, label: `${trust.orders_fulfilled.toLocaleString()} orders fulfilled` });
  if (trust.kyb_verified) items.push({ icon: BadgeCheck, label: "Verified business" });
  if (trust.insurance_on_file) items.push({ icon: ShieldCheck, label: "Insured" });
  return (
    <div data-testid="storefront-catalog-seller-info">
      {prof.tagline && <p className="text-white/75 text-[13px] mt-2 max-w-2xl leading-relaxed">{prof.tagline}</p>}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
          {items.map((it, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 text-white/70 text-[12px]">
              <it.icon className="w-3.5 h-3.5 opacity-80"/>{it.label}
            </span>
          ))}
        </div>
      )}
      <a href={`/s/${handle}`} data-testid="storefront-catalog-view-profile"
        className="inline-flex items-center gap-1.5 mt-3 text-[12.5px] font-medium text-white/85 hover:text-white hover:gap-2.5 transition-all">
        About this seller <ArrowRight className="w-3.5 h-3.5"/>
      </a>
    </div>
  );
}

function ProductDetailModal({ p, onClose, onAdd }) {
  if (!p) return null;
  const margin = p.retail_price ? Math.round((1 - p.price / p.retail_price) * 100) : null;
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="storefront-catalog-detail-modal">
        <DialogTitle className="display text-2xl tracking-tight">{p.name}</DialogTitle>
        <div className="grid sm:grid-cols-2 gap-5 mt-1">
          <div className="aspect-square rounded-2xl bg-secondary overflow-hidden grid place-items-center">
            {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover"/> : <Package className="w-8 h-8 text-foreground/30"/>}
          </div>
          <div>
            <div className="text-[11px] mono uppercase tracking-wider text-foreground/45">{p.category}{p.brand ? ` · ${p.brand}` : ""}</div>
            <div className="display text-3xl mt-2">{money(p.price)}</div>
            {p.retail_price && <div className="text-[12.5px] text-foreground/55 mt-1">Retail <span className="line-through">{money(p.retail_price)}</span>{margin ? <span className="ml-1 text-emerald-700">· {margin}% margin</span> : null}</div>}
            <div className="mt-4 grid grid-cols-2 gap-2 text-[12px] text-foreground/65">
              <div>SKU <span className="text-foreground mono">{p.sku || "—"}</span></div>
              <div>MOQ <span className="text-foreground">{p.moq || 1}</span></div>
              <div>Stock <span className="text-foreground">{p.stock ?? 0}</span></div>
              {p.case_size ? <div>Case <span className="text-foreground">{p.case_size}</span></div> : null}
              {p.sub_category ? <div>Sub-category <span className="text-foreground">{p.sub_category}</span></div> : null}
              {p.product_type ? <div>Type <span className="text-foreground">{p.product_type}</span></div> : null}
              {p.variant ? <div>Variant <span className="text-foreground">{p.variant}</span></div> : null}
              {p.size ? <div>Size <span className="text-foreground">{p.size}</span></div> : null}
              {p.gender ? <div>Gender <span className="text-foreground">{p.gender}</span></div> : null}
              {p.condition ? <div>Condition <span className="text-foreground">{p.condition}</span></div> : null}
              {p.exp_date ? <div>Exp date <span className="text-foreground">{p.exp_date}</span></div> : null}
              {p.upc ? <div>UPC <span className="text-foreground mono">{p.upc}</span></div> : null}
              {p.ean ? <div>EAN <span className="text-foreground mono">{p.ean}</span></div> : null}
              {p.asin ? <div>ASIN <span className="text-foreground mono">{p.asin}</span></div> : null}
              {(Array.isArray(p.custom_fields) ? p.custom_fields : Object.entries(p.custom_fields || {}).map(([label, value]) => ({ label, value }))).filter(c => c.label && c.value).map((c, i) => (
                <div key={i}>{c.label} <span className="text-foreground">{c.value}</span></div>
              ))}
            </div>
            {p.description && <p className="text-[12.5px] text-foreground/65 mt-3 leading-relaxed line-clamp-4">{p.description}</p>}
            <Button onClick={() => { onAdd(p, p.moq || 1); onClose(); }} data-testid="storefront-detail-add"
              className="w-full h-11 rounded-full bg-foreground text-background hover:bg-foreground/90 mt-4">
              Add to cart <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StorefrontSuccessModal({ data, onClose, onTrack }) {
  if (!data) return null;
  const { order, invoice } = data;
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-3xl border-border" data-testid="storefront-catalog-success-modal">
        <DialogTitle className="sr-only">Order placed</DialogTitle>
        <div className="relative bg-gradient-to-br from-emerald-50 via-white to-amber-50 px-7 pt-9 pb-7 border-b border-border">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-foreground/5 grid place-items-center text-foreground/50" data-testid="storefront-catalog-success-close"><X className="w-4 h-4"/></button>
          <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-sm"><CheckCircle2 className="w-6 h-6" strokeWidth={2.2}/></div>
          <div className="text-[10px] mono uppercase tracking-[0.22em] text-emerald-700/80 mt-5">Order placed</div>
          <h2 className="display text-2xl mt-1.5 tracking-tight">Your order is on its way to the seller</h2>
          <p className="text-[13px] text-foreground/65 mt-2">{order?.seller_name} received your purchase order. You'll get the final invoice once they confirm — no payment is due yet.</p>
        </div>
        <div className="px-7 py-5">
          <div className="rounded-xl border border-border bg-white px-4 py-3 flex items-center justify-between text-[12px] mono">
            <span>PO <span className="text-foreground font-medium">{order?.code}</span></span>
            <span>INV <span className="text-foreground font-medium">{invoice?.code}</span></span>
            <span className="display text-base">{money(order?.total)}</span>
          </div>
          <Button onClick={onTrack} className="w-full h-12 rounded-full bg-foreground text-background hover:bg-foreground/90 mt-4" data-testid="storefront-catalog-success-track">
            Track order status <ArrowRight className="w-4 h-4 ml-1.5"/>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
